import Card from "components/UI/Card/Card.js";
import CardHeader from "components/UI/Card/CardHeader.js";
import CardIcon from "components/UI/Card/CardIcon.js";
import CardBody from "components/UI/Card/CardBody.js";
import Skeleton from "react-loading-skeleton";

import { useTranslation } from "react-i18next";
import { TitlePages } from "components/UI/TitlePage/TitlePages";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import ReactTable from "components/Tables/ReactTable/ReactTable";
import { Settings } from "@mui/icons-material";
import GridContainer from "components/UI/Grid/GridContainer";
import {
  IActivityInfos,
  // IGetActivityItemsConfig,
  IProfiles,
  // ISetActivityItemsConfig,
} from "services/input-core/activity-items-config/@types";
import { useActivityItemConfigProfile } from "services/input-core/activity-items-config/get-activity-items-config";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSetActivityItemsConfig } from "services/input-core/activity-items-config/set-level-activity-items-config";
import { toast } from "react-toastify";
import { useSubscription } from "store/auth/auth-store";

interface IPropsTabsValue {
  profile: IProfiles;
}

function ActivityItemSettings({ profile }: IPropsTabsValue) {
  const { t } = useTranslation([
    "activity-item-table",
    "list-errors",
    "list-messages",
  ]);

  const { subscription } = useSubscription();

  const [filters, setFilters] = useState([]);

  const {
    mutate: setActivityItemsConfig,
    isLoading: isSetingActivityItemsConfig,
  } = useSetActivityItemsConfig();

  const {
    data: activityConfigProfile,
    refetch: refetchActivityItemConfigProfile,
    isLoading,
  } = useActivityItemConfigProfile(profile);

  const [formattedDate, setFormattedData] = useState<IActivityInfos[] | []>([]);

  const [valuesChangeLevel, setValuesChangeLevel] = useState<
    { level: number; activityItemCode: string }[]
  >([]);

  useEffect(() => {
    setFormattedData(activityConfigProfile?.activityInfos ?? []);
  }, [activityConfigProfile]);

  useEffect(() => {
    setValuesChangeLevel([]);
  }, [profile]);

  const handleSelectChange = useCallback(
    (event: { target: { value: string } }, activityItemCode: string) => {
      setValuesChangeLevel((callback) => {
        const auxArray = callback;
        const auxSome = callback?.some(
          (currentValue) => currentValue.activityItemCode == activityItemCode,
        );

        if (callback.length <= 0) {
          return [
            {
              activityItemCode: activityItemCode,
              level: Number(event.target.value),
            },
          ];
        } else {
          if (auxSome) {
            return callback?.map((option) => {
              return option.activityItemCode == activityItemCode
                ? { ...option, level: Number(event.target.value) }
                : { ...option };
            });
          } else {
            auxArray.push({
              activityItemCode: activityItemCode,
              level: Number(event.target.value),
            });
            return auxArray;
          }
        }
      });

      setFormattedData((callback) => {
        const newData: IActivityInfos[] | [] =
          callback?.map((options) => {
            return options.code === activityItemCode
              ? {
                  ...options,
                  level: Number(event.target.value),
                }
              : { ...options };
          }) ?? [];

        return newData;
      });
    },
    [],
  );

  const renderOptionsLevel = useMemo(() => {
    const componentes = [];

    for (let i = 1; i <= Number(activityConfigProfile?.maxLevels); i++) {
      componentes.push(
        <MenuItem key={`activityConfig-${i}`} value={i}>
          {t(`level-${i}`)}
        </MenuItem>,
      );
    }

    return componentes;
  }, [activityConfigProfile?.maxLevels]);

  const submitChangesLevelStatus = useCallback(() => {
    toast.info(
      t("action-set-activity-item-config-profile", {
        ns: "activity-item-table",
      }),
    );

    setActivityItemsConfig(
      {
        data: valuesChangeLevel.map((options) => ({
          level: String(options.level),
          activityItemCode: options.activityItemCode,
        })),
        subscription: subscription.subscriptionId,
      },
      {
        onSuccess: () => {
          toast.success(
            t("success-set-activity-item-config-profile", {
              ns: "activity-item-table",
            }),
          );

          setValuesChangeLevel(() => []);

          refetchActivityItemConfigProfile();
        },
        onError: () => {
          toast.error(t("set-fail"));
        },
      },
    );
  }, [valuesChangeLevel]);

  const columns = [
    {
      Header: t("activity-code"),
      accessor: "code",
    },
    {
      Header: t("name"),
      accessor: "name",
    },
    {
      Header: t("position"),
      accessor: "level",
      Cell: ({ row }: any) => (
        <div className="actions-left">
          <TextField
            // value={checkChangeValueLevel(row.original.level, row.original.code)}
            value={row.original.level}
            onChange={(e) => handleSelectChange(e, row.original.code)}
            variant="standard"
            select
            style={{ fontSize: "1rem", fontWeight: "light", width: "70%" }}
          >
            {renderOptionsLevel}
          </TextField>
        </div>
      ),
      Filter: ({ column }: any) => {
        const { filterValue, setFilter } = column;

        const handleSelectChange = (event: { target: { value: string } }) => {
          setFilter(event.target.value || undefined);
        };

        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              marginBottom: "5px",
              height: "100%",
            }}
          >
            <TextField
              value={filterValue || ""}
              onChange={handleSelectChange}
              label={t("select-level")}
              variant="standard"
              select
              style={{
                fontSize: "14px",
                width: "100%",
                opacity: "1",
                fontFamily: "Roboto, Helvetica, Arial, sans-serif",
                fontWeight: 400,
                lineHeight: 1.42857,
                color: "#495057",
              }}
            >
              <MenuItem value="">Clear</MenuItem>
              {renderOptionsLevel}
            </TextField>
          </div>
        );
      },
    },
    {
      Header: "Status",
      accessor: "actions",
      enableColumnFilter: false,
      Cell: ({ row }: any) => {
        return valuesChangeLevel?.some(
          (currentValue) => currentValue.activityItemCode == row.original.code,
        ) ? (
          <div className="actions-right">
            <Typography color="#0d6efd" variant="subtitle2">
              Modificado
            </Typography>
          </div>
        ) : (
          <div></div>
        );
      },
    },
  ];

  return (
    <GridContainer>
      <Card>
        <CardHeader color="rose" icon>
          <CardIcon color="rose">
            <Settings />
          </CardIcon>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box width="100%" display="flex" justifyContent="flex-start">
              <TitlePages type="sidebar" title="activity-item-settings" />
            </Box>

            <Box mt={3} width="100%" display="flex" justifyContent="flex-end">
              <Button
                type="button"
                variant="contained"
                disabled={
                  valuesChangeLevel.length <= 0 || isSetingActivityItemsConfig
                }
                onClick={() => submitChangesLevelStatus()}
              >
                {t("save-modification")}
              </Button>
            </Box>
          </div>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Skeleton />
          ) : (
            <ReactTable
              filters={filters}
              setFilters={setFilters}
              cacheFilters={true}
              columns={columns}
              data={formattedDate}
              disabledLastFilterHeader={false}
              columnsNotTrimFilters={["name"]}
            />
          )}
        </CardBody>
      </Card>
    </GridContainer>
  );
}

export default ActivityItemSettings;
